import router from './router'
import { useAppStoreWithOut } from '@/store/modules/app'
import type { RouteRecordRaw } from 'vue-router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { NO_REDIRECT_WHITE_LIST, SUCCESS_CODE } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { addRedirectField } from '@/utils/routerHelper'
import { resolveDefaultHomePath, resolveSafeEntryPath } from '@/utils/defaultHome'
import { getUserRoleApi, getSSOTokenApi } from '@/api/login'
import { useStorage } from '@/hooks/web/useStorage'
import { $msgbox } from './utils/messageBox'
import { handleAuthFailure } from '@/utils/authFailure'
import { isIdleExemptRoute, isIdleExpired, touchActivity } from '@/utils/idleTimeout'

// 检查是否为生产环境
const isProd = import.meta.env.VITE_NODE_ENV === 'production'

const { setStorage } = useStorage()

const { start, done } = useNProgress()

const { loadStart, loadDone } = usePageLoading()

/** 路由切换导致 axios 请求被取消，不应误判为权限失败 */
const isNavigationCancelled = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false
  }
  const err = error as { code?: string; name?: string; message?: string }
  return (
    err.code === 'ERR_CANCELED' ||
    err.name === 'AbortError' ||
    err.name === 'CanceledError' ||
    (typeof err.message === 'string' && err.message.toLowerCase().includes('canceled'))
  )
}

/** 动态路由初始化单飞锁，避免并发守卫重复拉菜单 / 重复 addRoute */
let dynamicRoutesSetupPromise: Promise<boolean> | null = null

/**
 * 拉取 /auth/menu 并注册动态路由（全局仅一次并发）
 */
const setupDynamicRoutes = async (): Promise<boolean> => {
  if (dynamicRoutesSetupPromise) {
    return dynamicRoutesSetupPromise
  }

  dynamicRoutesSetupPromise = (async () => {
    const permissionStore = usePermissionStoreWithOut()
    const appStore = useAppStoreWithOut()
    const userStore = useUserStoreWithOut()

    if (permissionStore.getIsAddRouters) {
      return true
    }

    const res = await getUserRoleApi()
    if (res?.code !== SUCCESS_CODE || !res?.data) {
      throw new Error(res?.message || '获取用户权限失败')
    }

    setStorage('btnList', res.data.btnList)
    userStore.setUserData(res.data.account)
    userStore.setUserLoginExtend(res.data.loginExtend)
    const menu = Array.isArray(res.data)
      ? addRedirectField(res.data)
      : addRedirectField(res.data.menuList ?? [])

    if (appStore.getDynamicRouter) {
      if (appStore.serverDynamicRouter) {
        await permissionStore.generateRoutes('server', menu as AppCustomRouteRecordRaw[])
      } else {
        await permissionStore.generateRoutes('frontEnd', menu as string[])
      }
    } else {
      await permissionStore.generateRoutes('static')
    }

    permissionStore.getAddRouters.forEach((route) => {
      router.addRoute(route as unknown as RouteRecordRaw)
    })
    permissionStore.setIsAddRouters(true)
    return true
  })().finally(() => {
    dynamicRoutesSetupPromise = null
  })

  return dynamicRoutesSetupPromise
}

/** 权限失败后统一清理，避免 userInfo 残留触发假登录态 */
const clearAuthState = () => {
  const userStore = useUserStoreWithOut()
  const permissionStore = usePermissionStoreWithOut()
  userStore.setToken('')
  userStore.setRefreshToken('')
  userStore.setUserInfo(undefined)
  userStore.setRoleRouters([])
  permissionStore.resetDynamicRoutes()
  localStorage.removeItem('userInfo')
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
}

/** 动态路由就绪后的目标路径 */
const resolvePostSetupPath = (to: { path: string; query: Record<string, unknown> }, from: { query: Record<string, unknown> }) => {
  if (to.path === '/login') {
    const redirectQuery = to.query.redirect
    if (typeof redirectQuery === 'string' && redirectQuery) {
      return resolveSafeEntryPath(redirectQuery)
    }
    return resolveDefaultHomePath()
  }
  const redirectPath = from.query.redirect || to.path
  return resolveSafeEntryPath(String(redirectPath))
}

router.beforeEach(async (to, from, next) => {
  // 清除上次可能因导航中断而未配对的 loading，避免整页遮罩残留
  loadDone()
  start()
  loadStart()
  const permissionStore = usePermissionStoreWithOut()
  const userStore = useUserStoreWithOut()

  // 检查是否存在单点登录参数code
  const ssoCode = to.query.code

  // 仅以有效 token 判定登录态；仅有 userInfo 不算已登录，避免 /login ↔ / 空转
  const isLoggedIn = !!userStore.getToken

  // 空闲超时：登录页 / SSO 回调不检查；其余已登录路由超时则退出
  if (isLoggedIn && !isIdleExemptRoute(to) && !ssoCode && isIdleExpired()) {
    loadDone()
    done()
    handleAuthFailure({ reason: 'idle' })
    next(false)
    return
  }

  // 如果有单点登录参数，尝试单点登录（无论是否已有token）
  if (ssoCode) {
    try {
      // 清除之前的登录信息，确保单点登录能正常进行
      clearAuthState()
      localStorage.removeItem('isSSO')
      localStorage.removeItem('isSSOUser')

      const ssoResponse = await getSSOTokenApi({ code: String(ssoCode) })

      if (ssoResponse.code === 0 && ssoResponse.data) {
        userStore.setToken(ssoResponse.data.accessToken)
        userStore.setRefreshToken(ssoResponse.data.refreshToken)
        // SSO 登录成功：重置空闲计时
        touchActivity(true)
        localStorage.setItem('userInfo', JSON.stringify(ssoResponse.data.account || {}))
        localStorage.setItem('isSSO', 'true')
        localStorage.setItem('isSSOUser', 'true')

        await setupDynamicRoutes()

        const redirectPath = from.query.redirect || '/'
        const redirect = decodeURIComponent(String(redirectPath))
        const newTo = { ...to, query: { ...to.query } }
        delete newTo.query.code

        const nextData = newTo.path === redirect ? { ...newTo, replace: true } : { path: redirect }
        next(nextData)
        return
      }

      loadDone()
      done()
      await $msgbox.alert('跳转失败，即将关闭当前窗口')
      window.close()
      return
    } catch (error) {
      console.error('单点登录失败:', error)
      loadDone()
      done()
      // 单点登录失败，继续常规流程
    }
  }

  if (isLoggedIn) {
    // 动态路由已就绪：访问登录页时跳转到目标页，勿再重定向到 / 触发空转
    if (to.path === '/login') {
      if (permissionStore.getIsAddRouters) {
        next({ path: resolvePostSetupPath(to, from), replace: true })
        return
      }
      // isAddRouters=false 时落到下方 setupDynamicRoutes，不在 /login 与 / 之间 ping-pong
    }

    if (permissionStore.getIsAddRouters) {
      const defaultHome = resolveDefaultHomePath()
      const isHomeEntry =
        to.path === '/' || to.path === '/home' || to.path === '/statDashboard'
      // 已在目标首页时勿再 next 同路径，否则 /home → /home 死循环导致整页 loading 白屏
      if (isHomeEntry && to.path !== defaultHome) {
        next({ path: defaultHome, replace: true })
        return
      }
      next()
      return
    }

    try {
      await setupDynamicRoutes()
      const redirect = resolvePostSetupPath(to, from)
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect }
      next(nextData)
      return
    } catch (error) {
      if (isNavigationCancelled(error)) {
        // 导航已被新路由取代，中止本次守卫，勿清 token / 勿再重定向
        loadDone()
        done()
        next(false)
        return
      }

      console.error('获取用户权限失败:', error, to)
      loadDone()
      done()
      clearAuthState()

      if (isProd) {
        await $msgbox.alert('获取用户权限失败，请重新登录车管家平台访问')
        window.close()
        next(false)
      } else {
        next(`/login?redirect=${encodeURIComponent(to.path)}`)
      }
      return
    }
  } else {
    // 清理仅有 userInfo、无 token 的残留态
    if (localStorage.getItem('userInfo')) {
      localStorage.removeItem('userInfo')
    }

    if (NO_REDIRECT_WHITE_LIST.indexOf(to.path) !== -1) {
      next()
      return
    }

    if (ssoCode) {
      next()
      return
    }

    if (isProd) {
      await $msgbox.alert('登录已过期，请重新登录车管家平台访问')
      window.close()
      next(false)
    } else {
      next(`/login?redirect=${encodeURIComponent(to.path)}`)
    }
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done()
  loadDone()
})

router.onError((error) => {
  console.error('路由错误:', error)
  done()
  loadDone()
})
